import * as logging from 'utils/Logging'

import { AccessTokenClaims, decodeAccessToken, isFresh } from 'utils/AccessToken'
import {
  AccessTokenResponse,
  MessageResponse,
  RefreshRequest,
  RefreshResponse,
  SignOutRequest,
} from './BorrowerPublicApiSchemas'
import { getDeviceId, getDeviceLabel } from 'utils/Device'

import { ErrorResponse } from './ErrorResponse'

/**
 * The subset of the generated public API client the session needs. Declared structurally so tests can hand
 * in a fake without standing up axios, and so the session never reaches for a client of its own.
 */
export interface AuthSessionApi {
  postSignInRequest(body: {
    email: string
    password: string
    deviceId: string
    deviceLabel?: string
  }): Promise<AccessTokenResponse>
  postRefreshRequest(body: RefreshRequest): Promise<RefreshResponse>
  postSignOutRequest(body: SignOutRequest): Promise<MessageResponse>
}

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated'

export interface AuthState {
  status: AuthStatus
  claims: AccessTokenClaims | null
  /**
   * Set when a live session ended without the customer asking — drives the notice on /sign-in. auth-service
   * answers the same 401 whether the cookie was displaced by another sign-in, revoked or simply expired, so
   * this says "not your doing", not "signed in elsewhere".
   */
  endedUnexpectedly: boolean
}

export type SignInFailure = 'invalid-credentials' | 'unavailable' | 'unknown'

export class SignInError extends Error {
  constructor(readonly reason: SignInFailure) {
    super(reason)
    this.name = 'SignInError'
  }
}

/** Thrown by [AuthSession.getOrRefreshToken] when there is no live session left to get a token from. */
export class NoActiveSessionError extends Error {
  constructor() {
    super('No active session')
    this.name = 'NoActiveSessionError'
  }
}

/**
 * Access tokens are persisted to `localStorage` so every tab shares one. auth-service rotates the refresh
 * token on each refresh and the cookie is shared browser-wide, so two tabs refreshing independently would
 * race and one would present an already-rotated cookie; sharing the access token keeps refreshes rare, and
 * [AuthSession.getOrRefreshToken] retries once to absorb the race that remains.
 */
const ACCESS_TOKEN_KEY = 'fluro_access_token'

/**
 * Records that this browser has held a session at some point, so a cold load can tell "no session yet"
 * from "session whose access token has expired".
 *
 * The `fluro_refresh` cookie is HttpOnly, so there is no way to ask the browser whether it holds one. That
 * left every anonymous visit to /sign-in firing a refresh that could only 401 — two of them, once the
 * retry is counted. This marker is the missing signal, and it carries nothing sensitive: it is a flag, not
 * a credential, and the cookie remains the only thing that can actually refresh a session (TED-4645).
 */
const HAD_SESSION_KEY = 'fluro_had_session'

/**
 * auth-service's 401 message when no refresh credential was presented at all, as opposed to one that was
 * presented and rejected. Kept in step with `NO_REFRESH_CREDENTIAL_MESSAGE` in auth-service's
 * `api/ApiErrors.kt`; tests on both sides pin the string, because it is the only part of the response that
 * distinguishes the two cases.
 */
const NO_REFRESH_CREDENTIAL_MESSAGE = 'No refresh credential presented.'

/**
 * A token inside this window of its expiry is treated as spent, so the refresh happens before a request
 * goes out rather than after cpa has rejected one. auth-service issues 10-minute tokens.
 */
const REFRESH_SKEW_SECONDS = 60

/**
 * Owns the customer's auth-service session in the browser (ADR-017 / TED-4200): the access token, the
 * refresh that keeps it alive, and sign-in / sign-out.
 *
 * Deliberately free of React so the refresh and rotation behaviour can be tested directly. The provider in
 * `AuthServiceConfig` adapts it to a hook.
 *
 * The refresh token itself is never visible here — it is the `fluro_refresh` HttpOnly cookie that
 * auth-service sets and the browser replays. That is why refresh is called with an empty body: the cookie
 * is the credential, and `RefreshResponse.refreshToken` is populated only for the mobile backend.
 */
export class AuthSession {
  private accessToken: string | null = readStoredToken()
  private state: AuthState = { status: 'unknown', claims: null, endedUnexpectedly: false }
  private listeners = new Set<(state: AuthState) => void>()
  private inFlightRefresh: Promise<string> | null = null
  /**
   * Backs [HAD_SESSION_KEY] for a browser that accepts reads but refuses writes (Safari private mode, a
   * full quota, some embedded webviews). There nothing throws, so the fail-open in [hasHadSession] never
   * fires and it would answer "no" for a session that is very much alive — costing the customer their
   * session the moment the access token went stale. Instance scope is page scope: there is one
   * `authSession`, and it is the same scope [accessToken] already covers.
   */
  private hadSessionThisPage = false

  constructor(private readonly api: AuthSessionApi) {}

  currentState(): AuthState {
    return this.state
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Settles the initial status on boot. A held token that is still fresh is enough; otherwise we ask for a
   * refresh, because the `fluro_refresh` cookie outlives the access token by a wide margin (30 days against
   * 10 minutes) and a page reload should not cost the customer their session.
   */
  async restore(): Promise<AuthStatus> {
    const held = this.heldClaims()
    if (held && isFresh(held, REFRESH_SKEW_SECONDS)) {
      this.publish({ status: 'authenticated', claims: held, endedUnexpectedly: false })
      return 'authenticated'
    }

    // Never held a session in this browser, so there is no cookie to refresh with and the call could
    // only 401. This is the ordinary path for a first-time visitor and now costs no requests at all.
    if (!this.hasHadSession()) {
      this.forget({ endedUnexpectedly: false })
      return 'unauthenticated'
    }

    try {
      await this.refresh()
      return 'authenticated'
    } catch (error) {
      // A cookie the server no longer recognises has already been dealt with: `abandonSession` forgot the
      // session and flagged that it ended without the customer asking. Forgetting again here reset that
      // flag before /sign-in could read it, so a customer who discovered their displacement by reloading
      // never saw the explanation (TED-4648). Anything else — a network failure, a 5xx — never reached
      // `abandonSession`, so the session is cleared here, and it is not one the customer lost elsewhere.
      if (!(error instanceof NoActiveSessionError)) {
        this.forget({ endedUnexpectedly: false })
      }
      return 'unauthenticated'
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    let response: AccessTokenResponse
    try {
      response = await this.api.postSignInRequest({
        email,
        password,
        deviceId: getDeviceId(),
        deviceLabel: getDeviceLabel(),
      })
    } catch (error) {
      throw new SignInError(signInFailureFrom(error))
    }

    if (!this.adopt(response.accessToken)) {
      throw new SignInError('unknown')
    }
  }

  /**
   * Revokes the session server-side and clears local state. The local half runs even if the call fails — a
   * customer who pressed "log out" must end up signed out of this browser regardless, and auth-service
   * treats sign-out as idempotent so a stale session row is harmless.
   */
  async signOut(): Promise<void> {
    try {
      await this.api.postSignOutRequest({})
    } catch (error) {
      logging.logError('AuthSession.signOut', { name: String(error) })
    } finally {
      this.forget({ endedUnexpectedly: false })
    }
  }

  /**
   * A token good for at least [REFRESH_SKEW_SECONDS] more, refreshing first if needed.
   *
   * Concurrent callers share one refresh: without that, a dashboard firing six requests at once would send
   * six refreshes, five of which would present an already-rotated cookie and fail.
   */
  async getOrRefreshToken(): Promise<string> {
    const held = this.heldToken()
    if (held && isFresh(held.claims, REFRESH_SKEW_SECONDS)) {
      return held.token
    }
    if (!this.hasHadSession()) {
      // Signed out, or never signed in. Answer the way a refused refresh would rather than spending a
      // request to be told the same thing.
      throw new NoActiveSessionError()
    }
    return this.refresh()
  }

  private refresh(): Promise<string> {
    if (this.inFlightRefresh) return this.inFlightRefresh

    const attempt = this.refreshWithRetry().finally(() => {
      this.inFlightRefresh = null
    })
    this.inFlightRefresh = attempt
    return attempt
  }

  /**
   * Refreshes, retrying a 401 exactly once.
   *
   * The retry is not defensive padding: another tab refreshing at the same moment rotates the cookie, and a
   * request already in flight with the old value comes back 401 even though the session is alive and the
   * browser now holds the new cookie. Retrying picks it up. A session that is genuinely gone 401s twice and
   * we sign out — which is what displacement by another web sign-in looks like.
   */
  private async refreshWithRetry(): Promise<string> {
    try {
      return await this.refreshOnce()
    } catch (error) {
      if (!isUnauthorised(error)) throw error
      // The browser sent no cookie, so the retry would present the same nothing and fail identically.
      // Only a *rejected* credential can be the rotation race the retry exists for.
      if (isNoRefreshCredential(error)) return this.abandonSession()

      try {
        return await this.refreshOnce()
      } catch (retryError) {
        if (!isUnauthorised(retryError)) throw retryError
        return this.abandonSession()
      }
    }
  }

  /** Drops the session and reports it the way every exhausted refresh path does. */
  private abandonSession(): never {
    this.forget({ endedUnexpectedly: true })
    throw new NoActiveSessionError()
  }

  private async refreshOnce(): Promise<string> {
    // Empty body on purpose: web sends the fluro_refresh cookie, which auth-service prefers over a body
    // token. Only the mobile backend puts a refresh token in the body.
    const { accessToken } = await this.api.postRefreshRequest({})
    if (!this.adopt(accessToken)) {
      throw new Error('auth-service returned an access token that could not be decoded')
    }
    return accessToken
  }

  /** Takes ownership of a newly issued token. False means it could not be decoded, so it was discarded. */
  private adopt(accessToken: string): boolean {
    const claims = decodeAccessToken(accessToken)
    if (!claims) {
      // Without an `exp` we cannot tell when to renew it, so treat it as no session at all rather than
      // storing something that will silently stop working.
      logging.logError('AuthSession.adopt', { name: 'auth-service returned an undecodable access token' })
      this.forget({ endedUnexpectedly: false })
      return false
    }

    this.accessToken = accessToken
    writeStoredToken(accessToken)
    this.markHasHadSession()
    this.publish({ status: 'authenticated', claims, endedUnexpectedly: false })
    return true
  }

  private forget({ endedUnexpectedly }: { endedUnexpectedly: boolean }): void {
    this.accessToken = null
    clearStoredToken()
    this.clearHasHadSession()
    this.publish({ status: 'unauthenticated', claims: null, endedUnexpectedly })
  }

  /**
   * Whether this browser has held a session before, and so might hold a `fluro_refresh` cookie.
   *
   * A held access token counts as the same evidence as the marker: it can only be there because [adopt]
   * put it there. Checking both means an install that predates the marker still refreshes normally on its
   * first load after this change, rather than signing everyone out once.
   *
   * Fails **open**: a browser that refuses `localStorage` outright cannot answer, and answering "no" there
   * would sign such a customer out on every reload even though their cookie is live. Assuming yes just
   * restores the pre-TED-4645 behaviour of trying the refresh.
   */
  private hasHadSession(): boolean {
    if (this.hadSessionThisPage) return true
    try {
      return window.localStorage.getItem(HAD_SESSION_KEY) !== null || readStoredToken() !== null
    } catch {
      return true
    }
  }

  private markHasHadSession(): void {
    this.hadSessionThisPage = true
    try {
      window.localStorage.setItem(HAD_SESSION_KEY, '1')
    } catch {
      // Storage unavailable; `hadSessionThisPage` carries it for the life of the page instead.
    }
  }

  private clearHasHadSession(): void {
    this.hadSessionThisPage = false
    try {
      window.localStorage.removeItem(HAD_SESSION_KEY)
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }

  private heldToken(): { token: string; claims: AccessTokenClaims } | null {
    // Re-read storage each time so a token another tab refreshed into place is picked up here too.
    const token = readStoredToken() ?? this.accessToken
    if (!token) return null
    const claims = decodeAccessToken(token)
    return claims ? { token, claims } : null
  }

  private heldClaims(): AccessTokenClaims | null {
    return this.heldToken()?.claims ?? null
  }

  private publish(state: AuthState): void {
    this.state = state
    this.listeners.forEach((listener) => listener(state))
  }
}

function signInFailureFrom(error: unknown): SignInFailure {
  if (!(error instanceof ErrorResponse)) return 'unknown'
  if (error.status === 401) return 'invalid-credentials'
  // auth-service surfaces a provider outage as 502/503 and leaves the session alone, so this is worth
  // telling apart from bad credentials: trying again later may well work.
  if (error.status === 502 || error.status === 503) return 'unavailable'
  return 'unknown'
}

function isUnauthorised(error: unknown): boolean {
  return error instanceof ErrorResponse && error.status === 401
}

/** True for the 401 auth-service returns when the request carried no refresh credential at all. */
function isNoRefreshCredential(error: unknown): boolean {
  return error instanceof ErrorResponse && error.message === NO_REFRESH_CREDENTIAL_MESSAGE
}

/**
 * Whether this browser has held a session before, and so might hold a `fluro_refresh` cookie.
 *
 * A held access token counts as the same evidence as the marker: it can only be there because [adopt] put
 * it there. Checking both means an install that predates the marker still refreshes normally on its first
 * load after this change, rather than signing everyone out once.
 *
 * Fails **open**: a browser that refuses `localStorage` (private mode, site data blocked) cannot answer,
 * and answering "no" there would sign such a customer out on every reload even though their cookie is
 * live. Returning true in that case just restores the pre-TED-4645 behaviour of trying the refresh.
 */
function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(accessToken: string): void {
  try {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  } catch {
    // Storage unavailable. The session still works for this page — the in-memory copy carries it, and a
    // reload simply costs one extra refresh.
  }
}

function clearStoredToken(): void {
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
